import React from "react";
import {Button, Text} from "@nike/podium";
import {Check, AlertCircle} from "@nike/nike-design-system-icons";
import {useEffect} from "react";
import {BatchProgress} from "../../common/data/types";

type ConfirmDialogConfig = {
    title: string;
    adjBadgeClass: string;
    adjBadgeText: string;
    lcBadge?: { label: string; variant: 'yes' | 'no' };
    description: React.ReactNode;
};

const CONFIRM_CONFIGS: Record<'zero' | 'under' | 'over', ConfirmDialogConfig> = {
    zero: {
        title: 'WMS = 0 — Clean-up',
        adjBadgeClass: 'b-negadj',
        adjBadgeText: 'NEGADJ',
        lcBadge: { label: 'LC if bonded', variant: 'yes' },
        description: <>Produces <strong>negative adjustments</strong>. Where inventory is bonded, these will go to <strong>Local Clearance</strong>.</>,
    },
    over: {
        title: 'WMS > CI — Clean-up',
        adjBadgeClass: 'b-posadj',
        adjBadgeText: 'POSADJ',
        description: <>Produces <strong>positive adjustments</strong>. CI records are aligned upward to match physical WMS stock — no Local Clearance involved.</>,
    },
    under: {
        title: 'WMS < CI — Adjust All',
        adjBadgeClass: 'b-negadj',
        adjBadgeText: 'NEGADJ',
        lcBadge: { label: 'No LC', variant: 'no' },
        description: <>Produces <strong>negative adjustments</strong>. These do <strong>not</strong> go to Local Clearance — must be handled under <strong>special procedure</strong> via ADJALL transaction type.</>,
    },
};

type ConfirmActionDialogProps = {
    bucketKey: 'zero' | 'under' | 'over';
    recordCount: number;
    totalUnits: number;
    progress: BatchProgress | null;
    onConfirm: () => void;
    onClose: () => void;
};

type Phase = 'confirm' | 'processing' | 'done';

function getPhase(progress: BatchProgress | null): Phase {
    if (!progress) return 'confirm';
    if (progress.done) return 'done';
    return 'processing';
}

export function ConfirmActionDialog({bucketKey, recordCount, totalUnits, progress, onConfirm, onClose}: ConfirmActionDialogProps) {
    const config = CONFIRM_CONFIGS[bucketKey];
    const phase = getPhase(progress);

    const totalBatches = progress ? Math.ceil(progress.totalItems / 100) : 0;

    useEffect(() => {
        if (phase === 'done' && progress?.errors.length) {
            console.group(`[ImbalanceService] ${progress.errors.length} batch(es) failed for bucket "${bucketKey}"`);
            progress.errors.forEach(err => {
                console.error(
                    `Batch ${err.batchIndex >= 0 ? err.batchIndex + 1 : '?'} (items ${err.itemRange[0] + 1}–${err.itemRange[1]}):`,
                    err.error,
                );
            });
            console.groupEnd();
        }
    }, [phase, progress, bucketKey]);

    return (
        <div className="im-confirm-overlay" onClick={phase === 'processing' ? undefined : onClose}>
            <div className="im-confirm-dialog" onClick={e => e.stopPropagation()}>
                <div className="im-confirm-header">
                    <Text font="body-2" weight="bold" as="div">
                        {phase === 'confirm' && <>Confirm — {config.title}</>}
                        {phase === 'processing' && <>Processing — {config.title}</>}
                        {phase === 'done' && (
                            progress!.errors.length > 0
                                ? <>Completed with errors — {config.title}</>
                                : <>Done — {config.title}</>
                        )}
                    </Text>
                    <Text font="body-4" color="secondary" as="div">
                        {phase === 'confirm' && 'Review the details below before proceeding'}
                        {phase === 'processing' && `Sending ${totalBatches} batch${totalBatches !== 1 ? 'es' : ''} (max 100 items each)`}
                        {phase === 'done' && (
                            progress!.errors.length > 0
                                ? `${progress!.totalItems - progress!.failedItems} of ${progress!.totalItems} items succeeded`
                                : `All ${progress!.totalItems} items processed successfully`
                        )}
                    </Text>
                </div>

                <div className="im-confirm-body">
                    {phase === 'confirm' && (
                        <>
                            <div className="im-confirm-row">
                                <span className="im-confirm-label">RECORDS</span>
                                <span><strong>{recordCount}</strong> lines will be processed</span>
                            </div>
                            <div className="im-confirm-row">
                                <span className="im-confirm-label">TOTAL UNITS</span>
                                <span><strong>{totalUnits}</strong> units affected</span>
                            </div>
                            <div className="im-confirm-row">
                                <span className="im-confirm-label">ADJ. TYPE</span>
                                <span className="im-confirm-badges">
                                    <span className={`im-badge im-${config.adjBadgeClass}`}>{config.adjBadgeText}</span>
                                    {config.lcBadge && (
                                        <span className={`im-badge im-lc-${config.lcBadge.variant}`}>
                                            <span className={`im-lc-dot im-lc-dot-${config.lcBadge.variant}`} />
                                            {config.lcBadge.label}
                                        </span>
                                    )}
                                </span>
                            </div>
                            <div className="im-confirm-row">
                                <span className="im-confirm-label">BATCHES</span>
                                <span><strong>{Math.ceil(recordCount / 100)}</strong> batch{Math.ceil(recordCount / 100) !== 1 ? 'es' : ''} of up to 100 items</span>
                            </div>
                            <div className="im-confirm-desc">
                                {config.description}
                            </div>
                        </>
                    )}

                    {phase === 'processing' && progress && (
                        <div className="im-dlg-progress-section">
                            <div className="im-dlg-progress-bar">
                                <div
                                    className="im-dlg-progress-fill"
                                    style={{width: `${progress.percentage}%`}}
                                />
                            </div>
                            <div className="im-dlg-progress-stats">
                                <Text font="body-3" weight="bold" as="span">
                                    {progress.percentage}%
                                </Text>
                                <Text font="body-4" color="secondary" as="span">
                                    {progress.completedItems} / {progress.totalItems} items
                                </Text>
                            </div>
                        </div>
                    )}

                    {phase === 'done' && progress && (
                        <>
                            {progress.errors.length === 0 ? (
                                <div className="im-dlg-success">
                                    <Check size="s" />
                                    <Text font="body-3" weight="medium" as="span">
                                        {progress.totalItems} items processed successfully
                                    </Text>
                                </div>
                            ) : (
                                <div className="im-dlg-partial-summary">
                                    {progress.totalItems - progress.failedItems > 0 && (
                                        <div className="im-dlg-partial-ok">
                                            <Check size="s" />
                                            <Text font="body-4" as="span">
                                                <strong>{progress.totalItems - progress.failedItems}</strong> items adjusted successfully
                                            </Text>
                                        </div>
                                    )}
                                    <div className="im-dlg-partial-fail">
                                        <AlertCircle size="s" />
                                        <Text font="body-4" as="span">
                                            <strong>{progress.failedItems}</strong> items could not be adjusted and remain in the bucket for retry
                                        </Text>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="im-confirm-footer">
                    {phase === 'confirm' && (
                        <>
                            <Button size="s" variant="outlined" onClick={onClose}>Cancel</Button>
                            <Button size="s" onClick={onConfirm}>Confirm &amp; proceed</Button>
                        </>
                    )}
                    {phase === 'processing' && (
                        <Button size="s" variant="outlined" disabled>
                            Processing…
                        </Button>
                    )}
                    {phase === 'done' && (
                        <Button size="s" onClick={onClose}>Close</Button>
                    )}
                </div>
            </div>
        </div>
    );
}
